import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend-by-laplacian-pyramid/definition"
import {assertNoBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"

export const generateGraph = (parameters: ImageOpParametersOfType<typeof Definition, "imgproc">) => {
    const {backgroundImage, foregroundImage, maskImage, wrapAround} = parameters
    if (wrapAround) {
        throw new Error("Wrap around is not supported yet.")
    }
    if (
        backgroundImage.descriptor.width !== foregroundImage.descriptor.width ||
        backgroundImage.descriptor.height !== foregroundImage.descriptor.height ||
        backgroundImage.descriptor.channels !== foregroundImage.descriptor.channels ||
        backgroundImage.descriptor.width !== maskImage.descriptor.width ||
        backgroundImage.descriptor.height !== maskImage.descriptor.height ||
        maskImage.descriptor.channels !== 1
    ) {
        throw new Error("All images must have the same dimensions and number of channels (except the mask which needs a single channel).")
    }
    const backgroundImagePyramid = buildImagePyramid(backgroundImage)
    const foregroundImagePyramid = buildImagePyramid(foregroundImage)
    const maskImagePyramid = buildImagePyramid(maskImage)
    const lastLevel = backgroundImagePyramid.length - 1
    let result = blendImages(
        backgroundImagePyramid[lastLevel].node,
        foregroundImagePyramid[lastLevel].node,
        maskImagePyramid[lastLevel].node,
        backgroundImagePyramid[lastLevel].descriptor.channels,
    )
    for (let i = lastLevel - 1; i >= 0; i--) {
        const backgroundImageLevel = backgroundImagePyramid[i]
        const foregroundImageLevel = foregroundImagePyramid[i]
        const maskImageLevel = maskImagePyramid[i]
        const backgroundImagePrevLevel = backgroundImagePyramid[i + 1]
        const foregroundImagePrevLevel = foregroundImagePyramid[i + 1]
        const upsampledBackgroundImagePrevLevel = ImageProcessingNodes.resize(
            backgroundImagePrevLevel.node,
            backgroundImageLevel.descriptor.width,
            backgroundImageLevel.descriptor.height,
        )
        const upsampledForegroundImagePrevLevel = ImageProcessingNodes.resize(
            foregroundImagePrevLevel.node,
            foregroundImageLevel.descriptor.width,
            foregroundImageLevel.descriptor.height,
        )
        const backgroundDifference = ImageProcessingNodes.math("-", backgroundImageLevel.node, upsampledBackgroundImagePrevLevel)
        const foregroundDifference = ImageProcessingNodes.math("-", foregroundImageLevel.node, upsampledForegroundImagePrevLevel)
        const blendedDifference = blendImages(backgroundDifference, foregroundDifference, maskImageLevel.node, backgroundImageLevel.descriptor.channels)
        const upsampledResult = ImageProcessingNodes.resize(result, backgroundImageLevel.descriptor.width, backgroundImageLevel.descriptor.height)
        result = ImageProcessingNodes.math("+", upsampledResult, blendedDifference)
    }
    return createImageOpImgProc({...backgroundImage.descriptor}, result)
}

const blendImages = (
    backgroundImage: ImageProcessingNodes.ImageNode,
    foregroundImage: ImageProcessingNodes.ImageNode,
    maskImage: ImageProcessingNodes.ImageNode,
    channels: number,
) => {
    const backgroundChannels =
        channels === 1 ? [backgroundImage] : new Array(channels).fill(0).map((_, i) => ImageProcessingNodes.extractChannel(backgroundImage, i))
    const foregroundChannels =
        channels === 1 ? [foregroundImage] : new Array(channels).fill(0).map((_, i) => ImageProcessingNodes.extractChannel(foregroundImage, i))
    const resultChannels: ImageProcessingNodes.ImageNode[] = []
    for (let i = 0; i < backgroundChannels.length; i++) {
        const backgroundChannel = backgroundChannels[i]
        const foregroundChannel = foregroundChannels[i]
        if (!backgroundChannel || !foregroundChannel) {
            throw new Error(`Missing channel at index ${i}: backgroundChannel=${backgroundChannel}, foregroundChannel=${foregroundChannel}`)
        }
        const invertedMaskChannel = ImageProcessingNodes.math("-", ImageProcessingNodes.math("constLike", maskImage, 1), maskImage)
        const blendedChannel = ImageProcessingNodes.math(
            "+",
            ImageProcessingNodes.math("*", backgroundChannel, invertedMaskChannel),
            ImageProcessingNodes.math("*", foregroundChannel, maskImage),
        )
        resultChannels[i] = blendedChannel
    }
    let combinedChannels: ImageProcessingNodes.ImageNode
    switch (resultChannels.length) {
        case 1:
            combinedChannels = resultChannels[0]
            break
        case 3:
            combinedChannels = ImageProcessingNodes.combineChannels([resultChannels[0], resultChannels[1], resultChannels[2]], "RGB")
            break
        case 4:
            combinedChannels = ImageProcessingNodes.combineChannels([resultChannels[0], resultChannels[1], resultChannels[2], resultChannels[3]], "RGBA")
            break
        default:
            throw new Error(`Unsupported number of channels: ${resultChannels.length}`)
    }
    return combinedChannels
}

const buildImagePyramid = (sourceImage: ImageOpImgProc) => {
    assertNoBatching(sourceImage.descriptor)
    const smoothingKernel = [1 / 4, 2 / 4, 1 / 4] // binomial kernel for Gaussian smoothing
    //const smoothingKernel = [1 / 16, 4 / 16, 6 / 16, 4 / 16, 1 / 16]    // binomial kernel for Gaussian smoothing
    const minSize = Math.min(sourceImage.descriptor.width, sourceImage.descriptor.height)
    const lowerPow2Exp = Math.floor(Math.log2(minSize))
    const numMipLevels = Math.max(1, lowerPow2Exp - 2)
    const pyramid: ImageOpImgProc[] = [sourceImage]
    for (let i = 1; i < numMipLevels; i++) {
        const previousImage = pyramid[i - 1]
        const blurredImageH = ImageProcessingNodes.convolve(
            previousImage.node,
            {
                type: "constKernel",
                width: smoothingKernel.length,
                height: 1,
                data: smoothingKernel,
            },
            "renormalize",
        )
        const blurredImage = ImageProcessingNodes.convolve(
            blurredImageH,
            {
                type: "constKernel",
                width: 1,
                height: smoothingKernel.length,
                data: smoothingKernel,
            },
            "renormalize",
        )
        const nextWidth = Math.ceil(previousImage.descriptor.width / 2)
        const nextHeight = Math.ceil(previousImage.descriptor.height / 2)
        const newImage = createImageOpImgProc(
            {
                ...previousImage.descriptor,
                width: nextWidth,
                height: nextHeight,
            },
            ImageProcessingNodes.resize(blurredImage, nextWidth, nextHeight),
        )
        pyramid.push(newImage)
    }
    return pyramid
}
